import * as React from 'react'
import moment from 'moment'
import {
  formatCurrencyFromCents,
  ICompany,
  ILocation,
  ITransaction,
} from '@getgreenline/homi-shared'
import { WorldpayTransactionModels } from '@getgreenline/payments'
import { COLORS } from '../../constants/Colors'
import {
  CalendarOutlined,
  CreditCardOutlined,
  DollarCircleFilled,
  PrinterFilled,
} from '@ant-design/icons'
import { printHTML } from '../../utilities/helpers'
import { Modal } from 'antd-v4'

interface Props {
  company: ICompany
  location: ILocation
  transaction: ITransaction
  isFirstElement: boolean
}

const getAmount = (response: WorldpayTransactionModels.Internal.ITransactionResponseContract) => {
  const { isApproved, approvedAmount, totalAmount } = response

  let centValue = 0

  if (isApproved && approvedAmount !== undefined) {
    centValue = approvedAmount * 100
  }

  if (!approvedAmount && totalAmount !== undefined) {
    centValue = totalAmount * 100
  }

  return formatCurrencyFromCents(centValue)
}

export const WorldpayReceiptList = ({ company, location, transaction, isFirstElement }: Props) => {
  const [showTransactionReceiptModal, setShowTransactionReceiptModal] = React.useState(false)

  const worldpayResponse = WorldpayTransactionModels.Helper.getWorldpayMetaDataFields(transaction)
  const { accountNumber, transactionDateTime, emv, paymentType, cardLogo, wpTransactionNumber } =
    worldpayResponse

  const isInterac = cardLogo === 'Interac'

  const label = emv ? `${paymentType} ${emv.applicationLabel}` : paymentType

  const paymentTypeAndLabel = isInterac ? label : cardLogo

  const dollarAmount = getAmount(worldpayResponse)

  const printReceipt = async () => {
    // Print fucntion works only if there's HTML elements. So the modal should be up to create the receipt.
    await setShowTransactionReceiptModal(true)

    printHTML('worldpay-print-section', company.name)
  }

  return (
    <div style={{ ...styles.container, marginTop: isFirstElement ? 0 : 16 }}>
      <div style={styles.column}>
        <div style={{ ...styles.labelContainerWithIcon, ...styles.marginBottom }}>
          <CreditCardOutlined style={styles.iconContainer} />
          <strong>{paymentTypeAndLabel}</strong>
        </div>

        <div style={{ ...styles.labelContainer, ...styles.marginBottom }}>
          {accountNumber.slice(-4)} (last four digits)
        </div>

        <div style={styles.labelContainerWithIcon}>
          <DollarCircleFilled style={styles.iconContainer} />
          Amount: {dollarAmount}
        </div>
      </div>

      <div style={styles.column}>
        <div style={{ ...styles.labelContainerWithIcon, ...styles.marginBottom }}>
          <CalendarOutlined style={styles.iconContainer} />
          {moment(transactionDateTime).local().format('MMM DD, YYYY h:mm A')}
        </div>

        <div style={styles.labelContainer}>Auth #: {wpTransactionNumber || ''}</div>
      </div>

      <div style={styles.printIconView}>
        <PrinterFilled onClick={printReceipt} />
      </div>

      {showTransactionReceiptModal && (
        <Modal
          title='Worldpay receipt'
          centered
          visible={showTransactionReceiptModal}
          onOk={printReceipt}
          okText='Print'
          onCancel={() => setShowTransactionReceiptModal(false)}
          cancelText='Close'
        >
          <div id='worldpay-print-section' style={{ paddingLeft: 10, paddingRight: 10 }}>
            <div
              dangerouslySetInnerHTML={{
                __html: WorldpayTransactionModels.Helper.generateWorldpayReceiptHTML(
                  company,
                  location,
                  worldpayResponse,
                  WorldpayTransactionModels.Enums.WorldpayReceiptOption.REPRINT,
                  {
                    fontSize: {
                      default: '11px',
                      large: '16px',
                    },
                  },
                ),
              }}
            />
          </div>
        </Modal>
      )}
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'row',
    border: `1px solid ${COLORS.faintBlack}`,
    borderRadius: 4,
  },
  column: {
    flex: 1,
    padding: `12px 4px`,
  },
  labelContainerWithIcon: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  labelContainer: {
    paddingLeft: 36,
  },
  marginBottom: {
    marginBottom: 8,
  },
  iconContainer: {
    fontSize: 24,
    marginRight: 8,
    color: COLORS.green200,
  },
  printIconView: {
    display: 'flex',
    paddingRight: 20,
    alignItems: 'center',
    fontSize: 48,
    color: COLORS.green400,
  },
}
